import * as React from 'react'

import { SelectOption } from '@factorialco/deprecated-design-system'
import { DAY_TYPES } from 'config/Vars'
import type { Field, Form } from 'factorial-form'
import { ElectronicSickLeaveEmployeeFragment } from 'generated/resources/graphql'
import isEmpty from 'lodash/isEmpty'
import { IReactionDisposer, reaction } from 'mobx'
import moment from 'moment'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { dateToString } from 'modules/core/lib/dates'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import { LabourUnitsResult } from 'modules/timeoff/hooks/useLabourUnits'
import type DocumentForm from 'modules/timeoff/lib/DocumentForm'
import LeaveDayTypeForm from 'modules/timeoff/lib/LeaveDayTypeForm'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { useContractsVersion } from 'modules/timeoff/lib/contractsVersionContext'
import BlockedPeriodsToDateTree from 'modules/timeoff/lib/toDateTree/BlockedPeriods'
import CompanyHolidaysToDateTree from 'modules/timeoff/lib/toDateTree/CompanyHolidays'
import LeavesToDateTree from 'modules/timeoff/lib/toDateTree/Leaves'
import timeoffAllowances from 'modules/timeoff/store/collections/allowances'
import blockedPeriods from 'modules/timeoff/store/collections/blockedPeriods/blockedPeriods'
import companyHolidays from 'modules/timeoff/store/collections/companyHolidays'
import type { LeaveTypes } from 'modules/timeoff/store/collections/leaveTypes'
import leaves from 'modules/timeoff/store/collections/leaves'
import timeoffPolicies from 'modules/timeoff/store/collections/timeoffPolicies/policies'
import type Allowance from 'modules/timeoff/store/models/Allowance'
import type Leave from 'modules/timeoff/store/models/Leave'
import { HALF_DAY_TYPES } from 'modules/timeoff/store/models/Leave'

import { leaveConfirmationFields } from '../EditLeave/useLeaveData'
import MedicalLeave from './MedicalLeave'
import OtherLeavesForm from './OtherLeavesForm'

interface Props {
  form: LeaveForm
  readOnly?: boolean
  leaveTypeReadOnly?: boolean
  documentForm: DocumentForm
  customFieldsForms: Form[]
  leaveId?: number
  employee?: Employee | null | undefined
  leave?: Leave
  leaveTypes: LeaveTypes
  year: number
  showEmployeeSelector: boolean
  onSelectEmployee?: (Ids: number[] | (number | null | undefined)) => void
  selectedEmployeeIds?: number[]
  bulkAdd?: boolean
  onDocumentDropped?: () => void
  electronicSickLeaveEmployee: ElectronicSickLeaveEmployeeFragment | null | undefined
  leaveConfirmationDetails?: leaveConfirmationFields
  contractVersions: ContractVersions
  timeTaken?: string
  labourUnits: LabourUnitsResult
  isReviewOrEditMode?: boolean
}

const Content = ({
  form,
  readOnly = false,
  leaveTypeReadOnly = false,
  documentForm,
  customFieldsForms,
  employee,
  leave,
  leaveTypes,
  year,
  leaveId,
  showEmployeeSelector,
  onSelectEmployee,
  selectedEmployeeIds,
  bulkAdd = false,
  onDocumentDropped,
  electronicSickLeaveEmployee,
  leaveConfirmationDetails,
  contractVersions,
  timeTaken,
  labourUnits,
  isReviewOrEditMode,
}: Props) => {
  const { startsOn } = useContractsVersion()

  if (bulkAdd && !form.get('policy_id').value) {
    const policyId = timeoffPolicies.models.find((policy) => policy.attributes.main)?.attributes.id

    timeoffAllowances.fetch()
    form.get('policy_id').set(policyId)
  }

  const selectedLeaveType = leaveTypes.get(form.data().leave_type_id)

  const employeeInfo = (): Employee | null | undefined => {
    if (employee) return employee

    const employeeId = +form.get('employee_id').value

    if (employeeId) {
      const employeeFound = employees.find({
        id: employeeId,
      })

      if (!employeeFound) {
        throw Error(`Error finding employees with id = ${employeeId}`)
      }

      return employeeFound
    }

    return null
  }

  const isHalfDaysEnabled = (): boolean => {
    if (!selectedLeaveType) return false

    return selectedLeaveType.attributes.half_days_units_enabled
  }

  const isInHours = (): boolean => {
    if (!selectedLeaveType) return false

    if (bulkAdd) {
      const allowancesForLeave = allowancesForPolicy().filter((allowance) => {
        return selectedLeaveType.attributes.allowance_ids.includes(allowance.attributes.id)
      })

      if (allowancesForLeave.length === 0) return false

      return allowancesForLeave[0]?.isHourly ?? false
    }

    const info = employeeInfo()

    if (!info) return false

    return selectedLeaveType.isHourlyForEmployee(info)
  }

  const allowancesForPolicy = (): Allowance[] => {
    const policyField = form.get('policy_id')

    return timeoffAllowances.filter(
      (allowance) => allowance.attributes.timeoff_policy_id === parseInt(policyField.value)
    )
  }

  const dayTypeOptions = (): SelectOption[] => {
    const options: SelectOption[] = []

    if (isInHours()) {
      options.push({
        value: DAY_TYPES.hours,
        label: i18n.t('time_off.day_types.hours'),
      })

      if (hasFeature('dev_timeoff_forbid_asking_for_day_units')) {
        return options
      }
    }

    if (isHalfDaysEnabled()) {
      options.push({
        value: DAY_TYPES.half_day,
        label: i18n.t('time_off.day_types.half_day'),
      })
    }

    options.push({
      value: DAY_TYPES.days,
      label: i18n.t('time_off.day_types.days'),
    })

    return options
  }

  const dayType = (form: LeaveForm): string => {
    if (!isEmpty(form.get('half_day').value)) return DAY_TYPES.half_day

    if (
      form.get('hours_amount_in_cents').value ||
      (hasFeature('dev_timeoff_forbid_asking_for_day_units') && isInHours())
    ) {
      return DAY_TYPES.hours
    }

    return DAY_TYPES.days
  }

  const dayTypeForm = useLazyRef(
    () =>
      new LeaveDayTypeForm({
        day_type: dayType(form),
      })
  ).current

  const leaveTypeId = form.get('leave_type_id').value

  useAsyncData(async () => {
    if (!employee?.attributes.id || !leaveTypeId) return

    const prevYear = moment([year - 1]).startOf('year')
    const nextYear = moment([year + 2]).endOf('year')

    leaves.fetch({
      data: {
        employee_id: employee.attributes.id,
        start_on: dateToString(prevYear, 'backend'),
        finish_on: dateToString(nextYear, 'backend'),
      },
    })

    return blockedPeriods.fetch({
      data: {
        leave_type_id: leaveTypeId,
        employee_id: employee?.attributes.id,
        start_on: dateToString(prevYear, 'backend'),
        finish_on: dateToString(nextYear, 'backend'),
      },
    })
  }, [employee?.attributes?.id, leaveTypeId])

  const companyHolidaysMuncher = useCallback(() => {
    return new CompanyHolidaysToDateTree(companyHolidays.toArray()).munch()
  }, [])

  const blockedPeriodsMuncher = useCallback(() => {
    return new BlockedPeriodsToDateTree(blockedPeriods.toArray()).munch(year)
  }, [year])

  // When editing a leave, leaveId is defined. We need to make it possible to edit the leave date
  // hence, we filter away the leave we are editing from the ones to block
  const existingLeavesToBlockOverlap = leaves.filter((leave) =>
    leaveId
      ? leave.attributes.id !== Number(leaveId)
      : leave.attributes.leave_type_id === Number(leaveTypeId)
  )

  const leavesMuncher = () => {
    return new LeavesToDateTree(existingLeavesToBlockOverlap).munch()
  }

  useUnsafeEffect(() => {
    const dayTypeFormCurrent: Field = dayTypeForm.get('day_type')
    const dayTypeCurrent: string = dayType(form)

    if (dayTypeFormCurrent.value !== dayTypeCurrent) {
      dayTypeFormCurrent.set(dayTypeCurrent)
    }

    let handlerDayType: IReactionDisposer | null = reaction(
      () => dayTypeForm.get('day_type').value,
      (dayType: string) => {
        if (dayType !== DAY_TYPES.half_day) {
          form.get('half_day').set('')
        } else {
          form.get('half_day').set(HALF_DAY_TYPES.beggining_of_day)
        }

        if (dayType !== DAY_TYPES.hours) {
          form.get('hours_amount_in_cents').set('')
          form.get('start_time').set('')
        }
      }
    )

    // Listen changes on leave type and update the day type
    // this is necessary when the user select a leave type by hours and we show
    // the hours option.
    // If the user changes the leave type again to a not hours option, the selector
    // (day_type) changes to days instead
    let handlerLeaveTypeId: IReactionDisposer | null = reaction(
      () => form.get('leave_type_id').value + form.get('policy_id').value,
      (_leaveTypeId: string) => {
        const dayType = isInHours() ? DAY_TYPES.hours : DAY_TYPES.days
        dayTypeForm.get('day_type').set(dayType)
      }
    )

    return () => {
      if (handlerDayType) {
        handlerDayType()
        handlerDayType = null
      }

      if (handlerLeaveTypeId) {
        handlerLeaveTypeId()
        handlerLeaveTypeId = null
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, dayTypeForm, leaveTypeId])

  const today = moment()

  const hasMedicalLeavesOpen = (employeeId: number): boolean => {
    return leaves.models.some((l: Leave) => {
      const leaveType = l.leaveType

      if (!leaveType) return false

      return (
        l.attributes.employee_id === employeeId &&
        leaveType.isMedicalLeave &&
        moment(l.attributes.finish_on).isSameOrAfter(today)
      )
    })
  }

  const isMedicalLeave = (): boolean => {
    if (leave) {
      return !!leave.attributes.medical_leave_id
    }

    const leaveTypeId = form.data().leave_type_id

    if (!leaveTypeId) return false
    if (!leaveTypes.length) return false
    if (!leaveTypes.get(leaveTypeId)) return false

    return leaveTypes.isMedicalLeave(leaveTypeId)
  }

  const selectableEmployees = (): Employee[] => {
    const activeEmployees = employees.active.sort()

    if (!isMedicalLeave()) {
      return activeEmployees.filter(
        (employee) =>
          employee.attributes.timeoff_policy_id === parseInt(form.get('policy_id').value)
      )
    }

    return activeEmployees.filter((e) => !hasMedicalLeavesOpen(e.id) && e.isEligibleForPayroll)
  }

  if (!isMedicalLeave()) {
    return (
      <OtherLeavesForm
        readOnly={readOnly}
        leaveTypeReadOnly={leaveTypeReadOnly}
        form={form}
        documentForm={documentForm}
        customFieldsForms={customFieldsForms}
        dayTypeForm={dayTypeForm}
        dayTypeOptions={dayTypeOptions}
        leave={leave}
        leaveTypes={leaveTypes}
        onDocumentDropped={onDocumentDropped}
        bulkAdd={bulkAdd}
        employee={employeeInfo()}
        showEmployeeSelector={showEmployeeSelector}
        onSelectEmployee={onSelectEmployee}
        selectedEmployeeIds={selectedEmployeeIds}
        isMedicalLeave={false}
        selectableEmployees={selectableEmployees()}
        hasMedicalLeavesOpen={hasMedicalLeavesOpen}
        allowancesForPolicy={allowancesForPolicy}
        companyHolidaysMuncher={companyHolidaysMuncher}
        blockedPeriodsMuncher={blockedPeriodsMuncher}
        leavesMuncher={leavesMuncher}
        selectedLeaveType={selectedLeaveType}
        electronicSickLeaveEmployee={electronicSickLeaveEmployee}
        leaveConfirmationDetails={leaveConfirmationDetails}
        contractVersions={contractVersions}
        timeTaken={timeTaken}
        labourUnits={labourUnits}
        isReviewOrEditMode={Boolean(isReviewOrEditMode)}
      />
    )
  }

  return (
    <MedicalLeave
      contractStartsOn={startsOn}
      year={year}
      leave={leave}
      leaveType={leaveTypes.get(form.data().leave_type_id)}
      leaveTypes={leaveTypes}
      form={form}
      readOnly={readOnly}
      employee={employeeInfo()}
      holidays={companyHolidaysMuncher}
      bulkAdd={bulkAdd}
      showEmployeeSelector={showEmployeeSelector}
      onSelectEmployee={onSelectEmployee}
      selectedEmployeeIds={selectedEmployeeIds}
      isMedicalLeave
      selectableEmployees={selectableEmployees()}
      hasMedicalLeavesOpen={hasMedicalLeavesOpen}
      allowancesForPolicy={allowancesForPolicy}
      selectedLeaveType={selectedLeaveType}
      contractVersions={contractVersions}
    />
  )
}

export default observer(Content)
